// ============================================================
// src/hooks/useScheduledMeetingsCount.ts
// Changements :
// Note : collection 'meetings' (vérifiée dans votre Firestore)
//
// CORRIGÉ : la requête ne filtrait auparavant que par status +
// scheduledAt, sans jamais restreindre par utilisateur. Firestore
// évalue les règles de sécurité document par document — un broker
// (ni staff ni admin) qui liste TOUS les meetings de la plateforme
// se fait refuser dès le premier document où il n'apparaît ni dans
// participants ni comme createdBy (la règle firestore.rules sur
// /meetings/{meetingId} exige l'un des deux). Ajout du filtre
// where('participants', 'array-contains', userId) pour que chaque
// document retourné passe la règle de sécurité existante — celle-ci
// n'a pas besoin d'être modifiée, c'est la requête qui doit se
// limiter aux meetings de l'utilisateur courant.
// ============================================================

import { useState, useEffect, useCallback } from 'react';
import {
  collection, query, where, orderBy,
  onSnapshot, Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';

interface ScheduledMeetingsData {
  count: number;
  upcomingMeeting?: {
    id: string;
    title: string;
    scheduledAt: Date;
    dealName?: string;
  };
}

export function useScheduledMeetingsCount(userId: string | undefined) {
  const [data, setData] = useState<ScheduledMeetingsData>({ count: 0 });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!userId) { setData({ count: 0 }); setIsLoading(false); return; }

    const now = new Date();

    // Ordre des champs dans la requête ci-dessous = ordre attendu par
    // l'index composite Firestore : participants (Arrays) → status
    // (Ascending) → scheduledAt (Ascending). Voir la console Firebase
    // pour créer cet index avec ces 3 champs dans cet ordre exact.
    const q = query(
      collection(db, 'meetings'),
      where('participants', 'array-contains', userId),
      where('status', '==', 'scheduled'),
      where('scheduledAt', '>=', Timestamp.fromDate(now)),
      orderBy('scheduledAt', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const count = snapshot.size;
      let upcomingMeeting: ScheduledMeetingsData['upcomingMeeting'];

      if (!snapshot.empty) {
        const next = snapshot.docs[0];
        const d = next.data();
        upcomingMeeting = {
          id: next.id,
          title: d.title,
          scheduledAt: d.scheduledAt instanceof Timestamp
            ? d.scheduledAt.toDate()
            : new Date(d.scheduledAt),
          dealName: d.dealName || d.deal?.business_name,
        };
      }

      setData({ count, upcomingMeeting });
      setIsLoading(false);
    }, (error) => {
      console.error('Error loading scheduled meetings:', error);
      setData({ count: 0 });
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [userId]);

  return { ...data, isLoading };
}